import React, { useState } from 'react';
import { Languages, Check, Loader2 } from 'lucide-react';
import { CHAT_LANGUAGES, setConversationLanguage, setConversationAgentsNativeLanguage } from '../../services/conversationService';
import type { ChatSession } from './types';
import { TYPE } from '../../constants/typeScale';

/**
 * Group main-language picker + native-language toggle (owner-only). The picker sets ONE language for
 * the group; the toggle instead lets EACH agent reply in its own native tongue (a reader can translate
 * any non-English bubble back to English).
 */
const ChatLanguageSetting: React.FC<{ session: ChatSession }> = ({ session }) => {
  const [value, setValue] = useState(session.language || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [native, setNative] = useState(!!session.agents_native_language);

  const save = async (lang: string) => {
    setValue(lang);
    setSaving(true); setSaved(false);
    try { const r = await setConversationLanguage(session.id, lang); setValue(r.language || ''); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    finally { setSaving(false); }
  };
  const toggleNative = async (on: boolean) => {
    setNative(on);
    try { const r = await setConversationAgentsNativeLanguage(session.id, on); setNative(r.agents_native_language); }
    catch { setNative(!on); }
  };

  return (
    <div className="mt-4">
      <div className="flex items-center gap-2">
        <Languages className="w-4 h-4 text-secondary-500 dark:text-secondary-300" />
        <span className={`${TYPE.body} font-medium text-secondary-800 dark:text-secondary-100`}>Chat language</span>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-secondary-400" />}
        {saved && !saving && <Check className="w-3.5 h-3.5 text-green-500" />}
      </div>
      <p className={`mt-1 ${TYPE.meta} text-secondary-400`}>
        Agents reply in this language by default. “Match me” lets each agent answer in whatever language you write.
      </p>
      <select
        value={value}
        onChange={(e) => save(e.target.value)}
        className={`mt-2 w-full rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 px-3 py-2 ${TYPE.body} text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500`}
      >
        {CHAT_LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>

      <label className="mt-3 flex items-start gap-2.5 cursor-pointer">
        <input type="checkbox" checked={native} onChange={(e) => toggleNative(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-secondary-300 dark:border-secondary-600" />
        <span>
          <span className={`block ${TYPE.body} font-medium text-secondary-800 dark:text-secondary-100`}>Let agents speak their native language</span>
          <span className={`block ${TYPE.meta} text-secondary-400`}>Each agent replies in its own native tongue; you can translate any message back to English.</span>
        </span>
      </label>
    </div>
  );
};

export default ChatLanguageSetting;
